import {
  Alert,
  Button,
  Container,
  Grid,
  Header,
  Select,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react';
import * as awsui from '@amzn/awsui-design-tokens/polaris.js';
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Challenge, ChallengeReviewableSection } from '@/src/types/Challenge';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { ChallengeReviewPopover } from '../../challengesCommon/ChallengeReviewPopover';

interface CreateTestEventProps {
  challenge: Challenge;

  /** Rejects if the deployment could not be started */
  onDeployTestEvent: (region: string | null) => Promise<void>;
}

const CreateTestEvent: React.FC<CreateTestEventProps> = ({ challenge, onDeployTestEvent }) => {
  const { t } = useTranslation();

  const regions: OptionDefinition[] = challenge.props.allowedRegions
    ? challenge.props.allowedRegions.map((region) => ({
        label: region,
        value: region.toLocaleLowerCase(),
      }))
    : [];

  const [readMore, setReadMore] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(regions[0]);
  const [attemptingDeployment, setAttemptingDeployment] = useState<boolean>(false);

  const onDeploy = () => {
    // Stays disabled on success; the view is replaced once the test event exists
    setAttemptingDeployment(true);

    onDeployTestEvent(selectedRegion?.value || null).catch(() => {
      setAttemptingDeployment(false);
    });
  };

  return (
    <SpaceBetween direction="vertical" size="m">
      <Container
        header={
          <Header variant="h2" actions={<ChallengeReviewPopover section={ChallengeReviewableSection.TESTING} />}>
            {t(i18nKeys.challenges.challengeDetails.headings.testing)}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <TextContent>
            <p>
              {t(i18nKeys.challenges.challengeDetails.text.oneClickTestEventDescription)}{' '}
              {!readMore && (
                <Button
                  variant="inline-link"
                  onClick={() => setReadMore(true)}
                  data-testid="challenge-testing-read-more"
                >
                  {t(i18nKeys.challenges.challengeDetails.text.readMore)}
                </Button>
              )}
            </p>
            {readMore && (
              <ul>
                <li>{t(i18nKeys.challenges.challengeDetails.text.clickTestEvent.one)}</li>
                <li>{t(i18nKeys.challenges.challengeDetails.text.clickTestEvent.two)}</li>
                <li>{t(i18nKeys.challenges.challengeDetails.text.clickTestEvent.three)}</li>
                <li>{t(i18nKeys.challenges.challengeDetails.text.clickTestEvent.four)}</li>
                <li>{t(i18nKeys.challenges.challengeDetails.text.clickTestEvent.five)}</li>
                <li>{t(i18nKeys.challenges.challengeDetails.text.clickTestEvent.six)}</li>
              </ul>
            )}
            {readMore && (
              <Button
                data-testid="challenge-testing-read-less"
                variant="inline-link"
                onClick={() => setReadMore(false)}
              >
                {t(i18nKeys.challenges.challengeDetails.text.readLess)}
              </Button>
            )}
          </TextContent>
        </SpaceBetween>
      </Container>
      <Container
        header={
          <Header variant="h2" actions={<ChallengeReviewPopover section={ChallengeReviewableSection.TESTING} />}>
            {t(i18nKeys.events.eventDetails.buttons.createTestEvent)}
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <div
            style={{
              backgroundColor: awsui.colorBackgroundCellShaded,
              borderRadius: '10px',
              padding: '10px',
            }}
          >
            <TextContent>
              <h3>{t(i18nKeys.challenges.challengeDetails.titles.chooseLabDeploymentRegion)}</h3>
              <p>{t(i18nKeys.challenges.challengeDetails.text.labDeploymentRegionDescription)}</p>
              <ul>
                <li>{t(i18nKeys.challenges.challengeDetails.text.labDeploymentRegionStepOne)}</li>
                <li>{t(i18nKeys.challenges.challengeDetails.text.labDeploymentRegionStepTwo)}</li>
              </ul>
            </TextContent>
            <Grid gridDefinition={[{ colspan: 5 }, { colspan: 7 }]}>
              <Select
                selectedOption={selectedRegion}
                onChange={({ detail }) => setSelectedRegion(detail.selectedOption)}
                options={regions}
                data-testid="challenge-testing-region"
              />
            </Grid>
          </div>
          <div
            style={{
              backgroundColor: awsui.colorBackgroundCellShaded,
              borderRadius: '10px',
              padding: '10px',
            }}
          >
            <Alert>{t(i18nKeys.challenges.challengeDetails.flashbar.oneClickTestEvent)}</Alert>
          </div>
          <div>
            <div style={{ float: 'right', paddingTop: '1rem' }}>
              <Button
                data-testid="challenge-testing-create-test"
                variant="primary"
                onClick={onDeploy}
                loading={attemptingDeployment}
              >
                {t(i18nKeys.challenges.challengeDetails.buttons.createOneClickTestEvent)}
              </Button>
            </div>
          </div>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default CreateTestEvent;
